import { StaticDataTable } from "@/app/home/components/table/StaticDataTable";
import { FilterSearchPayload } from "@/app/home/components/table/DataTable";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { invoiceStatusPipe } from "@/core/pipes/invoice-status.pipe";
import { WaterInvoiceBasic, WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import container from "@/lib/di/container";
import { Button, ConfigProvider, message, Modal, Radio } from "antd";
import { Dispatch, Key, SetStateAction, useEffect, useState } from "react";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

const EXPORT_EINVOICE_EXCLUDED_KEYS = new Set([
    "approveStatus",
    "debtCollector",
    "pageIndex",
    "pageSize",
]);

type ExportMode = "all" | "selected";

const skippedInvoicesMessage = (count: number) =>
    `Đã bỏ qua ${count} hóa đơn của khách hàng được cấu hình không phát hành hóa đơn điện tử.`;

const hasSkippedInvoicesMessage = (message?: string | null) =>
    message?.toLowerCase().includes("bỏ qua") ?? false;

interface ExportEInvoiceModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: {
        filterMap: Map<string, FilterSearchPayload>
        waterUserId?: string | null
    }
}

function buildExportParams(
    filterMap: Map<string, FilterSearchPayload>,
    waterUserId?: string | null
): Record<string, string> | null {
    const params: Record<string, string> = {};
    filterMap.forEach(filter => {
        if (
            filter.value !== undefined &&
            filter.value !== null &&
            filter.value !== "" &&
            !EXPORT_EINVOICE_EXCLUDED_KEYS.has(filter.key)
        ) {
            params[filter.key] = String(filter.value);
        }
    });
    if (waterUserId) {
        params.waterUserId = waterUserId;
    }
    if (!params.month || !params.year) {
        return null;
    }
    return params;
}

export const ExportEInvoiceModal: React.FC<ExportEInvoiceModalProps> = ({ isModalOpen, setIsModalOpen, data }) => {
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [exportMode, setExportMode] = useState<ExportMode>("all");
    const [selectedRows, setSelectedRows] = useState<WaterInvoiceBasic[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [invoices, setInvoices] = useState<WaterInvoiceBasic[]>([]);

    const exportParams = buildExportParams(data.filterMap, data.waterUserId);

    const loadData = async () => {
        if (!exportParams) {
            setInvoices([]);
            return;
        }
        setLoadingData(true);
        const records = (await waterInvoiceRepo.getAllBasic(exportParams)).map(r => ({
            ...r,
            waterInvoiceStatus: invoiceStatusPipe(r.waterInvoiceStatus),
        }));
        setInvoices(records);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setLoadingData(false);
    };

    useEffect(() => {
        loadData();
    }, [data.filterMap, data.waterUserId]);

    const handleCancel = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setExportMode("all");
        setIsModalOpen(false);
    };

    const exportAllByFilter = async () => {
        if (!exportParams) {
            message.error("Vui lòng chọn tháng và năm để xuất hoá đơn");
            return;
        }
        const result = await waterInvoiceRepo.ExportEInvoiceAllAsync(exportParams);
        if (!result) {
            message.error("Xuất hoá đơn thất bại");
            return;
        }
        if (result.success) {
            const resultMessage = [
                result.message,
                result.skipped > 0 && !hasSkippedInvoicesMessage(result.message)
                    ? skippedInvoicesMessage(result.skipped)
                    : null,
            ].filter(Boolean).join(" ");
            if (resultMessage) {
                message.info(resultMessage);
            } else {
                message.success("Xuất hoá đơn thành công");
            }
            setIsModalOpen(false);
            return;
        }
        message.warning(result.message || "Xuất hoá đơn thất bại");
    };

    const exportSelected = async () => {
        if (selectedRows.length === 0) {
            message.error("Vui lòng chọn ít nhất một hóa đơn");
            return;
        }
        const result = await waterInvoiceRepo.exportEInvoiceMany(selectedRows.map(r => r.id));
        if (result.success === 0 && result.skipped === 0) {
            message.error(result.lastError || "Xuất hoá đơn thất bại");
            return;
        }
        if (result.success > 0) {
            message.success(`Đã xuất thành công ${result.success}/${result.total} hoá đơn điện tử`);
        }
        if (result.skipped > 0) {
            message.info(skippedInvoicesMessage(result.skipped));
        }
        if (result.lastError) {
            message.warning(result.lastError);
        }
        setIsModalOpen(false);
    };

    const handleSave = async () => {
        setLoading(true);
        if (exportMode === "all") {
            await exportAllByFilter();
        } else {
            await exportSelected();
        }
        setLoading(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={1200}
                centered
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5 gap-2">
                        <Button key="cancel" onClick={handleCancel} disabled={loading}>
                            Huỷ
                        </Button>
                        <Button
                            key="submit"
                            type="primary"
                            loading={loading}
                            disabled={!exportParams || (exportMode === "selected" && selectedRows.length === 0)}
                            style={{
                                background: "var(--primary-color)",
                                paddingRight: 48,
                                paddingLeft: 48,
                                fontSize: 18,
                                fontWeight: 600,
                                paddingTop: 18,
                                paddingBottom: 18,
                            }}
                            onClick={handleSave}
                        >
                            {exportMode === "all"
                                ? "Xuất tất cả theo bộ lọc"
                                : `Xuất đã chọn (${selectedRows.length})`}
                        </Button>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">XUẤT HOÁ ĐƠN ĐIỆN TỬ</p>
                </div>

                {!exportParams ? (
                    <div className="mt-4 mx-2 text-red-500">Vui lòng chọn tháng và năm để xuất hoá đơn.</div>
                ) : (
                    <>
                        <div className="mt-4 mx-2">
                            <Radio.Group
                                value={exportMode}
                                onChange={(e) => setExportMode(e.target.value)}
                                options={[
                                    { value: "all", label: "Tất cả theo bộ lọc (chỉ HĐ chưa xuất HĐĐT)" },
                                    { value: "selected", label: "Theo danh sách đã chọn" },
                                ]}
                            />
                            <p className="mt-2 text-sm text-gray-500">
                                {exportMode === "all"
                                    ? `Sẽ xuất toàn bộ hoá đơn chưa có HĐĐT khớp bộ lọc tháng ${exportParams.month}/${exportParams.year}.`
                                    : `Đã chọn ${selectedRows.length}/${invoices.length} hoá đơn để xuất.`}
                            </p>
                        </div>
                        <div className="mt-[20px] mb-[10px]">
                            <StaticDataTable<WaterInvoiceBasic>
                                originalData={invoices}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                selectedRowKeys={selectedRowKeys}
                                setSelectedRowKeys={setSelectedRowKeys}
                                loading={loadingData}
                                columns={[
                                    { name: "Tháng", pipe: (value: WaterInvoiceBasic) => `${value.month}/${value.year}`, fixed: "left", width: 100 },
                                    { name: "Số TC", dataIndex: "code" },
                                    { name: "Mã KH", dataIndex: "waterUserCode" },
                                    { name: "Tên KH", dataIndex: "waterUserName", width: 350 },
                                    { name: "Tổng thanh toán", dataIndex: "total", pipe: formatCurrencyPipe },
                                    { name: "TT Thanh toán", dataIndex: "waterInvoiceStatus", width: 150 },
                                ]}
                            />
                        </div>
                    </>
                )}
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};
